// Boost Blenz Admin — Customers: the list and its Needs-a-person queue (SPEC §5.7), the full
// customer profile (§5.8), and Fix something (§5.9).
//
// 🔴 A FULL PAGE WITH ITS OWN URL, NOT A DRAWER. #/app/customers/<id> is what Support pastes into
// a thread; the design calls it the single biggest speed-up in the whole App section.
//
// 🔴 EVERY FIX IS A REAL TRACKER ROUTE, SIGNED AND EXPLAINED. The reason box is required (4+
// characters) and goes on the customer's record on both sides. Nothing here can log a dose —
// "I took it" is the customer's word, not ours — and the panel says so, so nobody asks.
import React from "react";
import {
  useT, Icon, Title, Card, Badge, Eyebrow, FIGTREE, DETOX, DETOX_ORDER, STATUS_META, useNarrow, usePeople,
  Body, Dot, SectionHead, Field, Empty, Note, Pill, LinkBtn, Modal, TextArea, PlainInput, SearchInput, Line, Bars,
  CalendarStrip, CalendarLegend, DAY_STATE, Timeline, fmtDay, fmtDayYear, fmtWhen, relDays, useToast,
} from "./ui";
import { useCachedJson } from "../../datacache";

type Go = (hash: string) => void;

const FILTERS: [string, string][] = [
  ["needs", "Needs a person"], ["all", "Everyone"], ["detoxing", "Detoxing"], ["waiting", "Waiting to start"],
  ["break", "On a break"], ["finished", "Finished"], ["never", "Never set up"],
];

/** Order numbers are typed every which way: #BB_14226, bb-14226, 14226. Compare the digits and letters. */
const orderKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "").replace(/^bb/, "");
export function matchesPerson(p: any, q: string) {
  const term = q.trim().toLowerCase();
  if (!term) return true;
  if (p.name?.toLowerCase().includes(term) || p.email?.toLowerCase().includes(term)) return true;
  const ok = orderKey(term);
  return !!ok && (p.orderNumbers || []).some((n: string) => orderKey(n).includes(ok));
}

export function whereLabel(p: any) {
  if (p.status === "detoxing" && p.stage) return `${DETOX[p.stage]?.label} day ${p.day} of ${p.stageLength}`;
  if (p.stage && (p.status === "waiting" || p.status === "break")) return `Next: ${DETOX[p.stage]?.label}`;
  return null;
}

// ================================================================== LIST
export function CustomersList({ go, filter }: { go: Go; filter: string }) {
  const t = useT();
  const res = usePeople();
  const [q, setQ] = React.useState("");
  const people: any[] = res.data?.people || [];
  const counts: Record<string, number> = {
    all: people.length,
    needs: people.filter((c) => c.needs.length).length,
  };
  for (const [id] of FILTERS) if (!(id in counts)) counts[id] = people.filter((c) => c.status === id).length;
  const active = FILTERS.some(([id]) => id === filter) ? filter : "needs";
  let rows = active === "all" ? people : active === "needs" ? people.filter((c) => c.needs.length) : people.filter((c) => c.status === active);
  rows = rows.filter((c) => matchesPerson(c, q));

  const setFilter = (id: string) => go("#/app/customers?filter=" + id);
  const th: React.CSSProperties = { textAlign: "left", padding: "13px 18px", borderBottom: "1px solid " + t.border, fontFamily: FIGTREE, fontWeight: 700, fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: t.muted, whiteSpace: "nowrap" };
  const td: React.CSSProperties = { padding: "13px 18px", borderBottom: "1px solid " + t.border, verticalAlign: "top" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <Card pad={16} style={{ borderRadius: t.tileRadius }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <SearchInput value={q} onChange={setQ} placeholder="Name, email, or order number" style={{ flex: "1 1 320px" }} />
          <Pill variant="ghost" size="sm" icon="refresh" onClick={res.refresh} disabled={res.revalidating}>{res.revalidating ? "Refreshing" : "Refresh"}</Pill>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 14 }}>
          {FILTERS.map(([id, label]) => {
            const on = active === id;
            const tone = id === "needs" && counts.needs ? t.bad : t.purple;
            return (
              <button key={id} onClick={() => setFilter(id)} aria-pressed={on}
                style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 11.5, letterSpacing: "0.04em", textTransform: "uppercase", padding: "7px 13px", borderRadius: 999, cursor: "pointer", border: "1px solid " + (on ? "transparent" : t.border), background: on ? tone : "transparent", color: on ? "#1a1033" : t.muted, display: "inline-flex", alignItems: "center", gap: 7 }}>
                {label}<span style={{ background: on ? "rgba(26,16,51,0.18)" : t.surface2, borderRadius: 999, padding: "1px 6px", fontSize: 10.5 }}>{res.data ? counts[id] : "…"}</span>
              </button>
            );
          })}
        </div>
      </Card>

      {active === "needs" && (
        <Note icon="alert" label="How someone lands here" tone="warn">
          Automation runs first: the app's own morning reminders and daily start nudges (milestone emails will join them once the Klaviyo Flows are built). Someone only appears here after <strong style={{ color: t.fg }}>7 days with no dose logged and no app visit</strong>, or straight away when something is broken on their account — reminders off, or a phone that can't receive a push, while a detox is running.
        </Note>
      )}

      <Card pad={0} style={{ overflow: "hidden" }}>
        {res.loading && !res.data ? (
          <div style={{ padding: 22, fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>Loading customers…</div>
        ) : res.data?.connected === false ? (
          <Empty icon="plug" title="Tracker not connected" action={<Pill icon="refresh" onClick={res.refresh}>Try again</Pill>}>The dashboard can't reach the tracker database. Customers are unaffected.</Empty>
        ) : res.error ? (
          <Empty icon="alert" title="Could not load customers" action={<Pill icon="refresh" onClick={res.refresh}>Retry</Pill>}>Something went wrong reading the tracker database.</Empty>
        ) : rows.length ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead>
                <tr>{["Customer", "Where they are", "Doses", "Last opened", "Reminders", active === "needs" ? "Why they need a person" : "Plan"].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {rows.slice(0, 200).map((c) => {
                  const [label, tone] = STATUS_META[c.status] || ["—", "neutral"];
                  const where = whereLabel(c);
                  return (
                    <tr key={c.id} onClick={() => go("#/app/customers/" + c.id)} style={{ cursor: "pointer" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = t.surface2; }} onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                      <td style={td}>
                        <a href={"#/app/customers/" + c.id} onClick={(e) => e.stopPropagation()} style={{ display: "block", fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg, textDecoration: "none" }}>{c.name}</a>
                        <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted }}>{c.email}</div>
                      </td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>
                        <Badge tone={tone}>{label}</Badge>
                        {where && <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, marginTop: 5 }}>{where}</div>}
                      </td>
                      <td style={{ ...td, fontFamily: FIGTREE, fontSize: 13, color: t.fg2, whiteSpace: "nowrap" }}>
                        {c.taken || c.missed ? <span>{c.taken} taken<span style={{ color: t.muted }}> · {c.missed} missed</span></span> : <span style={{ color: t.muted }}>None yet</span>}
                      </td>
                      <td style={{ ...td, fontFamily: FIGTREE, fontSize: 13, whiteSpace: "nowrap", color: c.daysSinceOpen != null && c.daysSinceOpen >= 3 ? t.bad : c.daysSinceOpen == null ? t.muted : t.fg2 }}>
                        {c.lastOpen ? relDays(c.daysSinceOpen) : "No visits recorded"}
                      </td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>
                        {c.remindersOn ? (c.hasPush ? <span style={{ fontFamily: FIGTREE, fontSize: 13, color: t.fg2 }}>On · {c.reminderLabel ?? "—"}</span> : <Badge tone="warn">No push</Badge>) : <Badge tone="bad">Off</Badge>}
                      </td>
                      <td style={{ ...td, fontFamily: FIGTREE, fontSize: 12.5, color: active === "needs" ? t.bad : t.muted, maxWidth: 300 }}>
                        {active === "needs"
                          ? c.needs[0]?.text
                          : !c.plan.length ? <span style={{ color: t.warn }}>Nothing claimed</span> : c.plan.length === 4 ? "All four detoxes" : c.plan.map((s: string) => DETOX[s]?.label).join(", ")}
                        {active === "needs" && c.needs.length > 1 && <div style={{ color: t.muted, fontSize: 11.5, marginTop: 2 }}>+{c.needs.length - 1} more</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.length > 200 && <div style={{ padding: "14px 18px", fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>Showing 200 of {rows.length}. Narrow it with search.</div>}
          </div>
        ) : (
          <Empty icon={active === "needs" && !q ? "check" : "search"} title={active === "needs" && !q ? "Nobody needs chasing" : "No customers match"}>
            {active === "needs" && !q
              ? "Everyone with a running detox is logging or opening the app, and nobody is stuck. Automation is handling the rest."
              : q ? `Nothing matches “${q}” here. Try Everyone, or an order number like #BB_14226.` : "No accounts in this state."}
          </Empty>
        )}
      </Card>
    </div>
  );
}

// ================================================================== FIX SOMETHING
type FixId = "code" | "link" | "start" | "unpark" | "break" | "setbreak" | "endbreak" | "time" | "notif";

function FixPanel({ prof, onDone }: { prof: any; onDone: (msg: string, ok: boolean) => void }) {
  const t = useT();
  const p = prof.person;
  const stages = prof.stagesRaw || {};
  const inPlan = DETOX_ORDER.filter((id) => p.plan.includes(id));
  const held = inPlan.filter((id) => stages[id]?.hold);
  const onBreak = inPlan.filter((id) => stages[id]?.snoozeUntil);
  // A mid-detox hold that's on (no resumedOn). "End the break" picks it back up today.
  const onHold = inPlan.filter((id) => (stages[id]?.pauses ?? []).some((pz: any) => !pz.resumedOn));
  // Where a break makes sense: the detox they're on, or one that hasn't started. Never a finished one.
  const breakable = inPlan.filter((id) => !stages[id]?.hold && (!stages[id]?.startedOn || prof.person.stage === id));
  const [breakId, setBreakId] = React.useState(prof.person.stage && breakable.includes(prof.person.stage) ? prof.person.stage : breakable[0] || "liver");
  const [breakUntil, setBreakUntil] = React.useState("");
  const [open, setOpen] = React.useState<FixId | null>(null);
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  // per-action inputs
  const [stageId, setStageId] = React.useState(inPlan.find((id) => stages[id]?.startedOn) || inPlan[0] || "liver");
  const [startedOn, setStartedOn] = React.useState("");
  const [time, setTime] = React.useState(p.reminderTime || "07:00");
  const [claim, setClaim] = React.useState<{ detoxes: string[]; binder: boolean; order: string }>({ detoxes: [], binder: false, order: "" });

  const FIXES: { id: FixId; label: string; why: string; show: boolean; unavailable?: string }[] = [
    { id: "code", label: "Resend their claim code", why: "Emails them a fresh 6-digit sign-in code, for when the last one went to spam or expired.", show: true,
      unavailable: "The app has no way to send this from the dashboard yet. For now, ask them to tap “Send a new code” on the sign-in screen — codes last 10 minutes and often land in junk." },
    { id: "link", label: "Link a purchase to this account", why: "Adds the detoxes they bought to their plan — the same thing their own Claim screen does. It can only add, never remove.", show: true },
    { id: "start", label: "Correct a detox start date", why: "Shifts the schedule. Every day after it — day numbers, binder days, reminders — moves with it.", show: inPlan.length > 0 },
    { id: "unpark", label: "Un-park a held detox", why: `Puts ${held.map((id) => DETOX[id].label).join(", ") || "a held stage"} back into the plan. It doesn't start it.`, show: held.length > 0 },
    { id: "setbreak", label: onHold.length ? "Extend their break" : "Set a break", why: onHold.length
        ? `Moves the check-in date on ${onHold.map((id) => DETOX[id].label).join(", ")}. The detox stays frozen where it stopped until they pick it back up.`
        : "For a customer going away. On the detox they're running, it freezes it where it is — no days are added, nothing is counted as missed, no reminders. On one they haven't started, it's a break before it. The app asks them on the date you pick.", show: breakable.length > 0 || onHold.length > 0 },
    { id: "endbreak", label: "End their break", why: `Picks ${onHold.map((id) => DETOX[id].label).join(", ") || "the detox"} back up today, at the day after where they stopped.`, show: onHold.length > 0 },
    { id: "break", label: "Clear a break", why: `Ends the break on ${onBreak.map((id) => DETOX[id].label).join(", ") || "a stage"} so the app can ask them about starting again.`, show: onBreak.length > 0 },
    { id: "time", label: "Change their reminder time", why: "Only when the customer asked. It's their setting, in their own timezone.", show: true },
    { id: "notif", label: p.remindersOn ? "Turn their reminders off" : "Turn their reminders back on", why: p.remindersOn ? "For a customer who asks us to stop. It stops every reminder; their phone stays registered, so a notice sent to everyone still reaches them." : "They'll start getting morning reminders again the next time the app syncs.", show: true },
  ];
  const fix = FIXES.find((f) => f.id === open);

  const post = async (path: string, body: Record<string, unknown>) => {
    const r = await fetch(`/api/app/customer/${encodeURIComponent(p.id)}/${path}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
    const j = await r.json().catch(() => ({}));
    return r.ok && j?.ok ? { ok: true } : { ok: false, error: j?.error || `The tracker refused it (${r.status}).` };
  };

  const ready = (() => {
    if (!fix || fix.unavailable) return false;
    if (reason.trim().length < 4) return false;
    if (fix.id === "start") return /^\d{4}-\d{2}-\d{2}$/.test(startedOn);
    if (fix.id === "time") return /^([01]\d|2[0-3]):[0-5]\d$/.test(time);
    if (fix.id === "link") return claim.detoxes.length > 0 || claim.binder;
    if (fix.id === "setbreak") return /^\d{4}-\d{2}-\d{2}$/.test(breakUntil);
    return true;
  })();

  async function run() {
    if (!fix) return;
    setBusy(true);
    setErr(null);
    const why = reason.trim();
    let res: { ok: boolean; error?: string } = { ok: false };
    if (fix.id === "start") res = await post("stage", { detoxId: stageId, startedOn, reason: why });
    else if (fix.id === "unpark") { for (const id of held) { res = await post("stage", { detoxId: id, hold: false, reason: why }); if (!res.ok) break; } }
    else if (fix.id === "break") { for (const id of onBreak) { res = await post("stage", { detoxId: id, snoozeUntil: null, reason: why }); if (!res.ok) break; } }
    else if (fix.id === "setbreak") res = await post("break", { detoxId: onHold[0] ?? breakId, until: breakUntil, reason: why });
    else if (fix.id === "endbreak") res = await post("break", { detoxId: onHold[0], end: true, reason: why });
    else if (fix.id === "time") res = await post("settings", { reminderTime: time, reason: why });
    else if (fix.id === "notif") res = await post("settings", { remindersOn: !p.remindersOn, reason: why });
    else if (fix.id === "link") {
      const all4 = claim.detoxes.length === 4;
      res = await post("claim", { detoxes: all4 ? [] : claim.detoxes, kit: all4, binder: claim.binder, reason: claim.order.trim() ? `${why} (order ${claim.order.trim()})` : why });
    }
    setBusy(false);
    if (res.ok) {
      onDone(`${fix.label} — saved and logged`, true);
      setOpen(null);
      setReason("");
    } else setErr(res.error || "That didn't go through. Nothing was changed.");
  }

  const sel: React.CSSProperties = { background: t.appBg, border: "1px solid " + t.borderStrong, borderRadius: 10, padding: "9px 11px", fontFamily: FIGTREE, fontSize: 13.5, color: t.fg, colorScheme: "dark" };

  return (
    <Card>
      <SectionHead title="Fix something" size={16} sub="Corrections to this one person's record. Each is logged with who did it and why." />
      <div style={{ display: "flex", flexDirection: "column" }}>
        {FIXES.filter((f) => f.show).map((f) => (
          <button key={f.id} onClick={() => { setOpen(f.id); setReason(""); setErr(null); }}
            style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", textAlign: "left", background: "none", border: "none", borderTop: "1px solid " + t.border, padding: "12px 0", cursor: "pointer", width: "100%" }}>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: FIGTREE, fontWeight: 600, fontSize: 13.5, color: f.unavailable ? t.muted : t.fg }}>
                {f.label}{f.unavailable && <Badge tone="neutral">Not built yet</Badge>}
              </span>
              <span style={{ display: "block", fontFamily: FIGTREE, fontSize: 12, color: t.muted, lineHeight: 1.45, marginTop: 2 }}>{f.why}</span>
            </span>
            <Icon name="chevron" size={14} color={t.muted} />
          </button>
        ))}
      </div>
      <Note icon="lock" label="Never available" style={{ marginTop: 16 }}>
        We don't log a dose on a customer's behalf, ever. A dose in the tracker means the customer said they took it. (Undoing a dose they logged by mistake is on the day itself, under Progress.)
      </Note>

      {fix && (
        <Modal title={fix.label} onClose={() => setOpen(null)}
          footer={fix.unavailable
            ? <Pill variant="ghost" onClick={() => setOpen(null)}>Close</Pill>
            : <><Pill variant="ghost" onClick={() => setOpen(null)}>Cancel</Pill><Pill disabled={!ready || busy} onClick={run}>{busy ? "Saving…" : "Save with reason"}</Pill></>}>
          <Body>{fix.why}</Body>
          <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 5 }}>
            <Eyebrow>Customer</Eyebrow>
            <Body style={{ color: t.fg }}>{p.name} · {p.email}</Body>
          </div>
          {fix.unavailable ? (
            <Note icon="info" style={{ marginTop: 16 }}>{fix.unavailable}</Note>
          ) : (
            <>
              {fix.id === "start" && (
                <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <select value={stageId} onChange={(e) => setStageId(e.target.value)} style={sel}>
                    {inPlan.map((id) => <option key={id} value={id}>{DETOX[id].label}{stages[id]?.startedOn ? ` · started ${fmtDay(stages[id].startedOn)}` : ""}</option>)}
                  </select>
                  <input type="date" value={startedOn} onChange={(e) => setStartedOn(e.target.value)} style={sel} aria-label="Actual start date" />
                </div>
              )}
              {fix.id === "setbreak" && (
                <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  {onHold.length ? (
                    <Body style={{ color: t.fg }}>{DETOX[onHold[0]].label} · on hold</Body>
                  ) : (
                    <select value={breakId} onChange={(e) => setBreakId(e.target.value)} style={sel}>
                      {breakable.map((id) => <option key={id} value={id}>{DETOX[id].label}{prof.person.stage === id && stages[id]?.startedOn ? " · running now" : " · not started"}</option>)}
                    </select>
                  )}
                  <input type="date" value={breakUntil} onChange={(e) => setBreakUntil(e.target.value)} style={sel} aria-label="Check in on" />
                  <Body size={12} color={t.muted} style={{ width: "100%" }}>Up to 3 months. The app asks them "Are you ready today?" on this date, in their own timezone ({p.timezone || "not sent by their phone"}).</Body>
                </div>
              )}
              {fix.id === "time" && (
                <div style={{ marginTop: 16 }}>
                  <input type="time" value={time} onChange={(e) => setTime(e.target.value)} style={sel} aria-label="Reminder time" />
                  <Body size={12} color={t.muted} style={{ marginTop: 6 }}>Their timezone: {p.timezone || "not sent by their phone"}</Body>
                </div>
              )}
              {fix.id === "link" && (
                <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
                  <Eyebrow>What they own</Eyebrow>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {DETOX_ORDER.map((id) => {
                      const on = claim.detoxes.includes(id);
                      const has = p.plan.includes(id);
                      return (
                        <button key={id} disabled={has} onClick={() => setClaim((c) => ({ ...c, detoxes: on ? c.detoxes.filter((x) => x !== id) : [...c.detoxes, id] }))}
                          title={has ? "Already on their plan" : undefined}
                          style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12, padding: "7px 13px", borderRadius: 999, cursor: has ? "not-allowed" : "pointer", border: "1px solid " + (on ? "transparent" : t.border), background: on ? DETOX[id].color : "transparent", color: on ? "#1a1033" : has ? t.muted : t.fg2, opacity: has ? 0.55 : 1 }}>
                          {DETOX[id].label}{has ? " ✓" : ""}
                        </button>
                      );
                    })}
                    <button onClick={() => setClaim((c) => ({ ...c, binder: !c.binder }))} disabled={p.ownsBinder}
                      style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12, padding: "7px 13px", borderRadius: 999, cursor: p.ownsBinder ? "not-allowed" : "pointer", border: "1px solid " + (claim.binder ? "transparent" : t.border), background: claim.binder ? "#ff8b7d" : "transparent", color: claim.binder ? "#1a1033" : t.fg2, opacity: p.ownsBinder ? 0.55 : 1 }}>
                      Binder{p.ownsBinder ? " ✓" : ""}
                    </button>
                  </div>
                  <PlainInput value={claim.order} onChange={(v: string) => setClaim((c) => ({ ...c, order: v }))} placeholder="Shopify order number (goes on the record), e.g. #BB_14226" />
                  <Body size={12} color={t.muted}>On a program already under way, anything new is parked for them to decide on — it is never slotted into the running detox.</Body>
                </div>
              )}
              <div style={{ marginTop: 16 }}>
                <Eyebrow style={{ marginBottom: 6 }}>Why are you doing this? (required)</Eyebrow>
                <TextArea value={reason} onChange={setReason} placeholder="e.g. Customer emailed — started a day early, schedule was off by one" />
                <Body size={12} color={t.muted} style={{ marginTop: 7 }}>Saved against your name and kept even if the account is later deleted.</Body>
              </div>
              {err && <div style={{ marginTop: 10, fontFamily: FIGTREE, fontSize: 12.5, color: t.bad }}>{err}</div>}
            </>
          )}
        </Modal>
      )}
    </Card>
  );
}

// ================================================================== PROFILE SECTIONS
function ProgressSection({ prof, onChanged }: any) {
  const t = useT();
  const started = prof.progress as any[];
  const [stage, setStage] = React.useState<string | null>(prof.focus || started[started.length - 1]?.stage || null);
  const [sel, setSel] = React.useState<any>(null);
  const [undoOpen, setUndoOpen] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const cur = started.find((s) => s.stage === stage);
  // Both numbers come from the SAME rendered cells, so the badge can never contradict the calendar.
  const cells = cur?.cells || [];
  const soFar = cells.filter((c: any) => c.state !== "future");
  const taken = cells.filter((c: any) => c.state === "taken").length;

  async function undo(kind: "detox" | "binder") {
    setBusy(true);
    setErr(null);
    const r = await fetch(`/api/app/customer/${encodeURIComponent(prof.person.id)}/undo`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ detoxId: cur.stage, day: sel.day, kind, reason: reason.trim() }) });
    const j = await r.json().catch(() => ({}));
    setBusy(false);
    if (r.ok && j?.ok) { setUndoOpen(false); setReason(""); setSel(null); onChanged("Dose undone — logged with your reason", true); }
    else setErr(j?.error || `The tracker refused it (${r.status}).`);
  }

  return (
    <Card>
      <SectionHead title="Progress" size={16}
        sub={cur ? `${DETOX[cur.stage].label} detox, day by day. Click a day for how it was logged.` : "No detox has started yet, so there's nothing logged."}
        right={started.length > 1 ? (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {started.map((s) => (
              <button key={s.stage} onClick={() => { setStage(s.stage); setSel(null); }} aria-pressed={s.stage === stage}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, padding: "5px 10px", borderRadius: 999, cursor: "pointer", border: "1px solid " + (s.stage === stage ? t.borderStrong : t.border), background: s.stage === stage ? t.surface2 : "transparent", color: s.stage === stage ? t.fg : t.muted }}>
                <Dot color={DETOX[s.stage].color} size={7} />{DETOX[s.stage].label}
              </button>
            ))}
          </div>
        ) : null} />
      {cur ? (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 11, flexWrap: "wrap" }}>
            <Dot color={DETOX[cur.stage].color} /><Title size={14}>{DETOX[cur.stage].label}</Title>
            <Badge tone="neutral">{taken} of {soFar.length} {soFar.length === 1 ? "day" : "days"} taken</Badge>
            <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted }}>Started {fmtDay(cur.startedOn)}</span>
          </div>
          <CalendarStrip days={cells} onPick={(d: any) => { setSel(sel?.day === d.day ? null : d); setUndoOpen(false); }} selected={sel?.day} />
          <CalendarLegend />
          {sel && (
            <div style={{ marginTop: 14, background: t.appBg, border: "1px solid " + t.border, borderRadius: 12, padding: 15 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <Title size={14}>Day {sel.day} · {fmtDay(sel.date)}</Title>
                <Badge tone={sel.state === "taken" ? "good" : sel.state === "missed" ? "bad" : "neutral"}>{DAY_STATE[sel.state]?.label}</Badge>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 14, marginTop: 12 }}>
                <Field label="Time taken">{sel.time || "—"}</Field>
                <Field label="How it was logged">{sel.how || "—"}</Field>
                <Field label="Binder day">{sel.binder ? (sel.binderState === "taken" ? `Yes — taken${sel.binderTime ? " " + sel.binderTime : ""}` : sel.binderState === "missed" ? "Yes — missed" : "Yes — not answered") : "No"}</Field>
                <Field label="Undone">{sel.undone ? "Yes — a dose was undone" : "No"}</Field>
              </div>
              {(sel.state === "taken" || sel.state === "missed" || sel.binderState === "taken" || sel.binderState === "missed") && (
                !undoOpen ? (
                  <button onClick={() => setUndoOpen(true)} style={{ marginTop: 12, background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: FIGTREE, fontWeight: 700, fontSize: 11.5, color: t.muted, textDecoration: "underline" }}>
                    Logged by mistake? Undo it…
                  </button>
                ) : (
                  <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
                    <Body size={12.5}>Undoing clears the answer for this day — it doesn't delete anything. Only do this when they tell you it was a mis-tap.</Body>
                    <TextArea rows={2} value={reason} onChange={setReason} placeholder="Why? (required) e.g. Customer says they tapped the wrong day" />
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {(sel.state === "taken" || sel.state === "missed") && <Pill size="sm" tone="bad" disabled={busy || reason.trim().length < 4} onClick={() => undo("detox")}>Undo the detox dose</Pill>}
                      {(sel.binderState === "taken" || sel.binderState === "missed") && <Pill size="sm" tone="bad" disabled={busy || reason.trim().length < 4} onClick={() => undo("binder")}>Undo the binder</Pill>}
                      <Pill size="sm" variant="ghost" onClick={() => setUndoOpen(false)}>Cancel</Pill>
                    </div>
                    {err && <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.bad }}>{err}</div>}
                  </div>
                )
              )}
            </div>
          )}
        </>
      ) : (
        <Empty icon="calendar" title="No doses yet">{prof.person.status === "waiting" ? "They haven't logged a first dose, so no detox has started." : "Nothing has been logged on this account."}</Empty>
      )}
    </Card>
  );
}

const STAGE_STATE: Record<string, [string, string]> = {
  running: ["Running", "good"], finished: ["Finished", "neutral"], break: ["On a break", "purple"], held: ["On hold", "purple"],
  waiting: ["Waiting to start", "warn"], parked: ["Parked", "bad"], decision: ["Waiting on a decision", "warn"],
};

function PlanSection({ prof, go }: any) {
  const t = useT();
  const p = prof.person;
  if (!p.plan.length) {
    return (
      <Card>
        <SectionHead title="Plan & timing" size={16} sub="Nothing is claimed on this account, so there's no schedule to show." />
        <div style={{ background: t.appBg, border: "1px solid " + t.border, borderRadius: 12, padding: 16 }}>
          <div style={{ display: "flex", gap: 9, alignItems: "center" }}><Icon name="alert" size={15} color={t.warn} /><Title size={14}>Nothing claimed yet</Title></div>
          <Body size={13.5} style={{ marginTop: 9 }}>{p.needs[0]?.text || "They made an account but never told the app what they own."}</Body>
          <Body size={13} color={t.muted} style={{ marginTop: 8 }}>
            {prof.orders.length
              ? <span>Order <strong style={{ color: t.fg }}>{prof.orders[0].number || prof.orders[0].id}</strong> is on this account, but nothing in it was claimed as a detox. Check what they bought and add it.</span>
              : "No Shopify order is attached to this email. Usually a gift, or they ordered under another email. Find the purchase in Shopify — for a gift, the buyer's order — and add what they own."}
          </Body>
          <Body size={12.5} color={t.muted} style={{ marginTop: 8 }}>Use <strong style={{ color: t.fg }}>Fix something → Link a purchase to this account</strong>. Their program appears as soon as it's saved.</Body>
        </div>
      </Card>
    );
  }
  return (
    <Card>
      <SectionHead title="Plan & timing" size={16} sub="What they own, when each detox is set to run, and the orders behind it." />
      <div style={{ display: "flex", flexDirection: "column" }}>
        {prof.stages.map((s: any, i: number) => {
          const [label, tone] = STAGE_STATE[s.state] || ["—", "neutral"];
          const when = s.state === "held" ? `On hold since ${fmtDay(s.holdSince)} · check-in ${fmtDay(s.holdUntil)}` : s.startedOn ? `Started ${fmtDay(s.startedOn)} · ends ${s.endsOn ? fmtDay(s.endsOn) : "—"}` : s.snoozeUntil ? `Break until ${fmtDay(s.snoozeUntil)}` : s.scheduledFor ? `Scheduled ${fmtDay(s.scheduledFor)}` : "Not scheduled";
          return (
            <div key={s.stage} style={{ display: "grid", gridTemplateColumns: "minmax(130px,1fr) auto auto", gap: 14, alignItems: "center", borderTop: i ? "1px solid " + t.border : "none", padding: "12px 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0, flexWrap: "wrap" }}>
                <Dot color={DETOX[s.stage].color} />
                <span style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 13.5, color: t.fg }}>{DETOX[s.stage].label}</span>
                <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted }}>{s.length} days</span>
              </div>
              <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, whiteSpace: "nowrap" }}>{when}</div>
              <Badge tone={tone}>{label}</Badge>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 18 }}>
        <Eyebrow style={{ marginBottom: 9 }}>Binder</Eyebrow>
        <Body>{p.ownsBinder ? "Owns the binder. Taken every other day from day 2, except during Heavy Metal, which has no binder days." : "No binder on this plan."}</Body>
      </div>
      <div style={{ marginTop: 18 }}>
        <Eyebrow style={{ marginBottom: 9 }}>Orders</Eyebrow>
        {prof.orders.length ? prof.orders.map((o: any) => (
          <div key={o.id} style={{ display: "flex", justifyContent: "space-between", gap: 12, borderTop: "1px solid " + t.border, padding: "11px 0", flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13, color: t.fg }}>{o.number || "Order " + o.id}</span>
              <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}> · {fmtDayYear(o.purchasedAt)}</span>
              <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>{o.items.length ? o.items.map((li: any) => `${li.title} ×${li.quantity}`).join(", ") : o.kit || "Line items not recorded"}</div>
            </div>
            <a href={`https://admin.shopify.com/store/boost-blenz/orders/${String(o.id).split("/").pop()}`} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
              <Pill size="sm" variant="ghost" icon="store">Open in Shopify</Pill>
            </a>
          </div>
        )) : <Note icon="alert" tone="warn">No Shopify order is linked. They claimed their products themselves, or the lookup found nothing at sign-up.</Note>}
      </div>
    </Card>
  );
}

function SettingsSection({ prof }: any) {
  const t = useT();
  const p = prof.person;
  return (
    <Card>
      <SectionHead title="Settings" size={16} sub="Everything the customer controls themselves." />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 18 }}>
        <Field label="Reminders">{p.remindersOn ? <Badge tone="good">On</Badge> : <Badge tone="bad">Off</Badge>}</Field>
        <Field label="Reminder time">{p.reminderLabel ?? "—"} <span style={{ color: t.muted, fontSize: 12 }}>their time</span></Field>
        <Field label="Can receive a push">{p.hasPush ? <Badge tone="good">Yes</Badge> : <Badge tone="bad">No</Badge>}</Field>
        <Field label="Timezone">{p.timezone || "Not sent"}</Field>
        <Field label="Morning routine">{p.routineLabel ?? <span style={{ color: t.muted }}>Hasn't told us</span>}</Field>
        <Field label="Takes medication">{p.medication == null ? <span style={{ color: t.muted }}>Hasn't told us</span> : p.medication ? "Yes" : "No"}</Field>
        <Field label="Disclaimer accepted"><span style={{ color: t.muted, fontSize: 12.5 }}>Kept on their phone only</span></Field>
        <Field label="Account created">{fmtDayYear(p.createdAt)}</Field>
      </div>
      {p.remindersOn && !p.hasPush && (
        <Note icon="alert" tone="warn" style={{ marginTop: 16 }}>
          Their switch is on but the phone can't receive a push — they denied notifications, or the address expired. Reminders are silently doing nothing. Ask them to allow notifications for Boost Blenz in their phone's settings.
        </Note>
      )}
      {p.status === "break" && p.phase?.kind === "between" && (
        <Note icon="info" style={{ marginTop: 12 }}>Reminders pause between detoxes by design. They'll get a start nudge each morning once the next detox is due.</Note>
      )}
      {p.medication && <Note icon="info" style={{ marginTop: 12 }}>Takes medication. This changes no timing; it's why the app shows them the medication disclaimer.</Note>}
    </Card>
  );
}

function ActivitySection({ prof }: any) {
  const t = useT();
  const p = prof.person;
  const anyOpens = prof.activity.some((a: any) => a.n);
  return (
    <Card>
      <SectionHead title="App activity" size={16} sub="Visits in the last 30 days, counted at most once per half hour. People stop opening the app days before they stop taking the capsules." />
      {anyOpens ? (
        <Line data={prof.activity.map((a: any) => ({ label: fmtDay(a.d), value: a.n }))} color={t.purple} height={110} />
      ) : (
        <Note icon="info">No app visits recorded in 30 days. Visits are recorded from Sep 13, 2026 — someone on an older build shows nothing here until they update.</Note>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 16, marginTop: 18 }}>
        <Field label="Last opened">{p.lastOpen ? `${relDays(p.daysSinceOpen)} · ${fmtWhen(p.lastOpen)}` : "No visits recorded"}</Field>
        <Field label="Last dose logged">{p.lastLog ? fmtWhen(p.lastLog) : "None yet"}</Field>
      </div>
      {prof.taps.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <Eyebrow style={{ marginBottom: 10 }}>Notifications they tapped</Eyebrow>
          <Bars rows={prof.taps.map((x: any) => ({ label: x.label, value: x.n, color: t.purple }))} showPct={false} height={8} />
        </div>
      )}
    </Card>
  );
}

function QuestionsSupportSection({ prof, go }: any) {
  const t = useT();
  const email = prof.person.email;
  const threads = useCachedJson<any>(email ? `/api/app/profile/${encodeURIComponent(prof.person.id)}/support` : null);
  const list: any[] = threads.data?.threads || [];
  const label: Record<string, [string, string]> = { ai: ["Answered by AI", "purple"], faq: ["Answered from the FAQ", "good"], none: ["Not answered", "bad"] };
  const items = [
    ...prof.questions.map((q: any) => ({ kind: "q", at: q.at, q })),
    ...list.map((th) => ({ kind: "th", at: th.receivedAt, th })),
  ].sort((a, b) => String(b.at).localeCompare(String(a.at)));
  return (
    <Card>
      <SectionHead title="Questions & support" size={16} sub="What they asked inside the app, and the emails they sent us — one list." />
      {items.length ? (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {items.map((it, i) => it.kind === "q" ? (
            <div key={"q" + i} style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", borderTop: i ? "1px solid " + t.border : "none", padding: "12px 0", flexWrap: "wrap" }}>
              <div style={{ minWidth: 200, flex: 1 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 5 }}><Badge tone="neutral">Asked in the app</Badge><span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted }}>{fmtDay(it.q.at)}</span></div>
                <Body size={13.5} style={{ color: t.fg }}>{it.q.question || <em style={{ color: t.muted }}>(empty question)</em>}</Body>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <Badge tone={label[it.q.answered]?.[1] ?? "neutral"}>{label[it.q.answered]?.[0] ?? it.q.answered}</Badge>
                {it.q.escalated && <Badge tone="warn">Sent to the team</Badge>}
              </div>
            </div>
          ) : (
            <button key={it.th.account + it.th.threadId} onClick={() => go(`#/support/${it.th.account}/${it.th.threadId}`)}
              style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", width: "100%", textAlign: "left", background: "none", border: "none", borderTop: i ? "1px solid " + t.border : "none", padding: "12px 0", cursor: "pointer", flexWrap: "wrap" }}>
              <span style={{ minWidth: 200, flex: 1 }}>
                <span style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 5 }}><Badge tone="accent">Emailed support</Badge><span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted }}>{fmtDay(it.th.receivedAt)}</span></span>
                <span style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.fg }}>{it.th.subject || "(no subject)"}</span>
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Badge tone={it.th.answered ? "good" : "bad"}>{it.th.answered ? "Answered" : "Needs response"}</Badge><Icon name="arrowRight" size={14} color={t.purple} /></span>
            </button>
          ))}
        </div>
      ) : threads.loading ? (
        <Body size={12.5} color={t.muted}>Checking Help Desk…</Body>
      ) : (
        <Empty icon="question" title="They haven't asked anything">No in-app questions and no support emails from {email} in the last year.</Empty>
      )}
    </Card>
  );
}

function EmailsSection({ prof }: any) {
  const t = useT();
  return (
    <Card>
      <SectionHead title="Emails we sent" size={16} sub="Milestones the app handed to Klaviyo, and whether each one made it there." />
      {prof.emails.length ? prof.emails.map((e: any, i: number) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", borderTop: i ? "1px solid " + t.border : "none", padding: "11px 0" }}>
          <div><div style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 13.5, color: t.fg }}>{e.label}</div><div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted }}>{fmtWhen(e.at)}</div></div>
          <Badge tone={e.delivered ? "good" : "warn"}>{e.delivered ? "Reached Klaviyo" : "Waiting to send"}</Badge>
        </div>
      )) : <Empty icon="mail" title="No milestones yet">Nothing has triggered a milestone on this account.</Empty>}
      <Note icon="info" style={{ marginTop: 14 }}>“Reached Klaviyo” means Klaviyo has the event. Whether an email goes out is up to the matching Klaviyo Flow — as of Sep 13, 2026 those flows aren't built yet.</Note>
    </Card>
  );
}

const EVENT_WORDS: Record<string, string> = {
  "answer:start_scheduled": "Picked a start date",
  "answer:first_dose": "Logged a first dose",
  "answer:new_order_add": "Added a new order to their plan",
  "answer:new_order_hold": "Put a new order on hold",
  started: "Program started", paused: "Paused", resumed: "Resumed", resupplied: "Reordered", completed: "Finished the plan",
};
const ACTION_WORDS: Record<string, string> = { stage: "Changed a detox's timing", settings: "Changed their settings", undo: "Undid a dose", claim: "Added products to their plan" };

function HistorySection({ prof }: any) {
  const t = useT();
  const p = prof.person;
  const team = prof.team.map((x: any) => ({
    at: x.at, when: fmtDay(x.at), color: t.accent,
    text: ACTION_WORDS[x.action] || x.action,
    detail: describeMeta(x.action, x.meta),
    by: `${x.actor.split("@")[0]} · “${x.reason}”`,
  }));
  const ev: any[] = [];
  if (p.createdAt) ev.push({ at: p.createdAt, text: "Account created", color: t.purple });
  for (const o of prof.orders) if (o.purchasedAt) ev.push({ at: o.purchasedAt, text: `Order ${o.number || o.id} placed`, color: t.accent });
  for (const s of prof.stages) if (s.startedOn) ev.push({ at: s.startedOn, text: `Started ${DETOX[s.stage].label} detox`, color: DETOX[s.stage].color });
  for (const e of prof.events) ev.push({ at: e.at, text: EVENT_WORDS[e.type] || e.type.replace(/[:_]/g, " "), color: t.good });
  for (const e of prof.emails) ev.push({ at: e.at, text: e.label, detail: e.delivered ? "Milestone handed to Klaviyo" : "Milestone waiting to send", color: t.warn });
  for (const q of prof.questions) ev.push({ at: q.at, text: `Asked Help: “${q.question}”`, detail: q.answered === "none" ? "Not answered" : undefined, color: t.purple });
  for (const u of prof.undone) ev.push({ at: u.at, text: `${DETOX[u.detoxId]?.label ?? u.detoxId} day ${u.day} ${u.kind} undone`, color: t.bad });
  ev.sort((a, b) => String(b.at).localeCompare(String(a.at)));
  return (
    <Card>
      <SectionHead title="History" size={16} sub="Team changes first — who did it and why — then everything that happened on this account, newest first." />
      {team.length ? <Timeline events={team} /> : <Body size={12.5} color={t.muted} style={{ marginBottom: 12 }}>Nobody on the team has changed this record.</Body>}
      {team.length > 0 && <div style={{ height: 1, background: t.border, margin: "6px 0 10px" }} />}
      <Timeline events={ev.slice(0, 60).map((e) => ({ ...e, when: fmtDay(e.at) }))} />
    </Card>
  );
}
function describeMeta(action: string, meta: any): string | undefined {
  if (!meta) return undefined;
  if (action === "stage" && meta.detox) {
    const bits = Object.entries(meta.patch || {}).map(([k, v]) => `${k === "startedOn" ? "start date" : k === "snoozeUntil" ? "break" : k === "hold" ? "parked" : k} → ${v ?? "cleared"}`);
    return `${DETOX[meta.detox]?.label ?? meta.detox}: ${bits.join(", ")}`;
  }
  if (action === "settings" && meta.patch) return Object.entries(meta.patch).map(([k, v]) => `${k === "remindersOn" ? "reminders" : k === "reminderTime" ? "reminder time" : k} → ${v}`).join(", ");
  if (action === "undo") return `${DETOX[meta.detox]?.label ?? meta.detox} day ${meta.day} ${meta.kind}`;
  return undefined;
}

// ================================================================== PROFILE
function ProfileHeader({ prof, go, onEmail, onCopy, narrow }: any) {
  const t = useT();
  const p = prof.person;
  const [label, tone] = STATUS_META[p.status] || ["—", "neutral"];
  return (
    <Card pad={narrow ? 16 : 20}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 18, flexWrap: "wrap" }}>
        <div style={{ minWidth: 240, flex: 1 }}>
          <LinkBtn icon="back" onClick={() => go("#/app/customers")} style={{ marginBottom: 10 }}>All customers</LinkBtn>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <Title size={narrow ? 22 : 27}>{p.name}</Title><Badge tone={tone}>{label}</Badge>
          </div>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 8, alignItems: "center" }}>
            <span style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.fg2 }}>{p.email}</span>
            {prof.orders[0] && (
              <a href={`https://admin.shopify.com/store/boost-blenz/orders/${String(prof.orders[0].id).split("/").pop()}`} target="_blank" rel="noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap", fontFamily: FIGTREE, fontWeight: 700, fontSize: 12, color: t.purple, textDecoration: "none" }}>
                <Icon name="store" size={13} color={t.purple} />{prof.orders[0].number || "Order"} in Shopify
              </a>
            )}
          </div>
          <Body size={14.5} style={{ marginTop: 14, maxWidth: 660, color: t.fg }}>{p.summary}</Body>
          {p.flags.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 14 }}>
              {p.flags.map((f: any, i: number) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <Icon name="alert" size={14} color={p.needs.some((n: any) => n.code === f.code) ? t.bad : t.warn} />
                  <span style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 13, color: p.needs.some((n: any) => n.code === f.code) ? t.bad : t.warn }}>{f.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 9, alignItems: "stretch", minWidth: narrow ? "100%" : 200, flex: narrow ? "1 1 100%" : "0 0 auto" }}>
          <Pill icon="mail" onClick={onEmail}>Email them</Pill>
          <Pill variant="ghost" size="sm" icon="link" onClick={onCopy}>Copy a link to this page</Pill>
        </div>
      </div>
    </Card>
  );
}

function EmailModal({ p, onClose, onSent }: any) {
  const t = useT();
  const first = p.name.split(" ")[0];
  const [subject, setSubject] = React.useState("Checking in on your detox");
  const [body, setBody] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const templates: [string, string, string][] = [
    ["Checking in", "Checking in on your detox", `Hi ${first},\n\nWe noticed you haven't opened the Detox Tracker in a few days — just checking everything's going okay. If anything is getting in the way, reply here and we'll help.\n\nThe Boost Blenz team`],
    ["Reminders explained", "About your reminders", `Hi ${first},\n\nYour morning reminders pause between detoxes on purpose, and start again when your next one is due. If they've stopped during a detox, check that notifications are allowed for Boost Blenz in your phone's settings.\n\nThe Boost Blenz team`],
    ["Ready to start?", "Ready to start your next detox?", `Hi ${first},\n\nYour next detox is ready whenever you are. Open the Detox Tracker and pick a start morning — we'll remind you when it's time.\n\nThe Boost Blenz team`],
  ];
  async function send() {
    setBusy(true);
    setErr(null);
    const r = await fetch(`/api/app/profile/${encodeURIComponent(p.id)}/email`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ subject, text: body }) });
    const j = await r.json().catch(() => ({}));
    setBusy(false);
    if (r.ok && j?.ok) onSent(j);
    else setErr(j?.error || `Couldn't send (${r.status}). Nothing went out.`);
  }
  return (
    <Modal title={"Email " + p.name} onClose={onClose} width={580}
      footer={<><Pill variant="ghost" onClick={onClose}>Cancel</Pill><Pill icon="send" disabled={busy || !subject.trim() || body.trim().length < 4} onClick={send}>{busy ? "Sending…" : "Send"}</Pill></>}>
      <Note icon="mail" label="Where this ends up">
        Sent from support@boostblenz.com, never a personal inbox. Their reply lands in <strong style={{ color: t.fg }}>Help Desk</strong> like any customer email, and shows under <strong style={{ color: t.fg }}>Questions &amp; support</strong> on this profile.
      </Note>
      <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 5 }}><Eyebrow>To</Eyebrow><Body style={{ color: t.fg }}>{p.email}</Body></div>
      <div style={{ marginTop: 14 }}><Eyebrow style={{ marginBottom: 6 }}>Subject</Eyebrow><PlainInput value={subject} onChange={setSubject} maxLength={200} /></div>
      <div style={{ marginTop: 14 }}>
        <Eyebrow style={{ marginBottom: 6 }}>Message</Eyebrow>
        <TextArea rows={7} value={body} onChange={setBody} placeholder={`Hi ${first} — `} />
      </div>
      <div style={{ display: "flex", gap: 7, marginTop: 12, flexWrap: "wrap" }}>
        {templates.map(([name, subj, text]) => <Pill key={name} size="sm" variant="soft" tone="purple" onClick={() => { setSubject(subj); setBody(text); }}>{name}</Pill>)}
      </div>
      {err && <div style={{ marginTop: 10, fontFamily: FIGTREE, fontSize: 12.5, color: t.bad }}>{err}</div>}
    </Modal>
  );
}

export function CustomerProfile({ id, go }: { id: string; go: Go }) {
  const t = useT();
  const narrow = useNarrow();
  const [nonce, setNonce] = React.useState(0);
  const res = useCachedJson<any>(`/api/app/profile/${encodeURIComponent(id)}${nonce ? `?v=${nonce}` : ""}`);
  const [emailOpen, setEmailOpen] = React.useState(false);
  const { toast, toastNode } = useToast();
  const prof = res.data;

  if (res.loading && !prof) return <Card><Body color={t.muted}>Loading customer…</Body></Card>;
  if (res.error || !prof?.found) {
    return (
      <Card pad={0}>
        <Empty icon="search" title="Customer not found" action={<Pill onClick={() => go("#/app/customers")}>Back to customers</Pill>}>
          This account may have been deleted. Deleted accounts keep only the audit trail and anonymous counts.
        </Empty>
      </Card>
    );
  }

  const changed = (msg: string, ok: boolean) => { toast(msg, !ok); setNonce((n) => n + 1); };
  const copy = () => {
    const url = location.href;
    (navigator.clipboard?.writeText(url) ?? Promise.reject()).then(() => toast("Link copied — paste it into a Help Desk thread")).catch(() => toast(url));
  };
  const header = <ProfileHeader prof={prof} go={go} onEmail={() => setEmailOpen(true)} onCopy={copy} narrow={narrow} />;
  const modal = emailOpen && <EmailModal p={prof.person} onClose={() => setEmailOpen(false)} onSent={() => { setEmailOpen(false); toast("Sent from support@ — their reply will land in Help Desk"); }} />;

  // Narrow: one column, every section open, reordered for triage (SPEC §5.8).
  if (narrow) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {header}
        <SettingsSection prof={prof} />
        <ProgressSection key={"p" + nonce} prof={prof} onChanged={changed} />
        <QuestionsSupportSection prof={prof} go={go} />
        <PlanSection prof={prof} go={go} />
        <ActivitySection prof={prof} />
        <FixPanel key={"f" + nonce} prof={prof} onDone={changed} />
        <EmailsSection prof={prof} />
        <HistorySection prof={prof} />
        {modal}{toastNode}
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {header}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.6fr) minmax(0,1fr)", gap: 18, alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
          <ProgressSection key={"p" + nonce} prof={prof} onChanged={changed} />
          <PlanSection prof={prof} go={go} />
          <ActivitySection prof={prof} />
          <QuestionsSupportSection prof={prof} go={go} />
          <HistorySection prof={prof} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
          <SettingsSection prof={prof} />
          <FixPanel key={"f" + nonce} prof={prof} onDone={changed} />
          <EmailsSection prof={prof} />
        </div>
      </div>
      {modal}{toastNode}
    </div>
  );
}

// ================================================================== GLOBAL SEARCH
/** Header search, on every screen: name, email or Shopify order number → straight to the profile. */
export function GlobalSearch({ go }: { go: Go }) {
  const t = useT();
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [hi, setHi] = React.useState(0);
  const ref = React.useRef<HTMLDivElement>(null);
  const res = useCachedJson<any>(open || q ? "/api/app/people" : null);
  React.useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const term = q.trim();
  const hits: any[] = term ? (res.data?.people || []).filter((p: any) => matchesPerson(p, term)).slice(0, 7) : [];
  const pick = (p: any) => { go("#/app/customers/" + p.id); setOpen(false); setQ(""); };
  return (
    <div ref={ref} style={{ position: "relative", flex: "1 1 260px", maxWidth: 420, minWidth: 0 }}>
      <SearchInput value={q} onChange={(v: string) => { setQ(v); setOpen(true); setHi(0); }} onFocus={() => setOpen(true)} placeholder="Find a customer — name, email, or order #"
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, hits.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
          else if (e.key === "Enter" && hits[hi]) pick(hits[hi]);
          else if (e.key === "Escape") setOpen(false);
        }} />
      {open && term && (
        <div style={{ position: "absolute", top: "112%", left: 0, right: 0, zIndex: 90, background: t.surface, border: "1px solid " + t.borderStrong, borderRadius: 14, padding: 6, boxShadow: "0 18px 40px rgba(0,0,0,0.5)", minWidth: 280 }}>
          {res.loading && !res.data ? (
            <div style={{ padding: 12, fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>Searching…</div>
          ) : hits.length ? hits.map((p, i) => (
            <button key={p.id} onClick={() => pick(p)} onMouseEnter={() => setHi(i)}
              style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", width: "100%", textAlign: "left", background: i === hi ? t.surface2 : "none", border: "none", padding: "10px 12px", borderRadius: 9, cursor: "pointer" }}>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontFamily: FIGTREE, fontWeight: 700, fontSize: 13, color: t.fg }}>{p.name}</span>
                <span style={{ display: "block", fontFamily: FIGTREE, fontSize: 11.5, color: t.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.email}{p.orderNumbers?.length ? " · " + p.orderNumbers.slice(0, 2).join(", ") : ""}</span>
              </span>
              <Badge tone={STATUS_META[p.status]?.[1] ?? "neutral"}>{STATUS_META[p.status]?.[0] ?? "—"}</Badge>
            </button>
          )) : (
            <div style={{ padding: 12, fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>No tracker account matches “{term}”. Order numbers look like #BB_14226.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ================================================================== HELP DESK RAIL
/** "In the tracker" beside a Help Desk thread (SPEC §5.10). Matched by the sender's email. */
export function InTheTracker({ email, go, threadKey, compact }: { email: string; go: Go; threadKey?: string; compact?: boolean }) {
  const t = useT();
  const res = useCachedJson<any>(email ? `/api/app/lookup?email=${encodeURIComponent(email)}` : null);
  const others = useCachedJson<any>(email ? `/api/helpdesk/search?q=${encodeURIComponent("from:" + email)}` : null);
  if (!email) return null;
  if (res.error) return null; // no `customers` permission, or the lookup failed — the inbox still works
  const d = res.data;
  const otherThreads = (others.data?.threads || []).filter((th: any) => th.account + ":" + th.threadId !== threadKey);
  return (
    <div style={{ display: "grid", gridTemplateColumns: compact || !d?.found ? "1fr" : "minmax(0,1.5fr) minmax(0,1fr)", gap: 12, alignItems: "start" }}>
      <Card pad={16}>
        {!d ? (
          <Body size={12.5} color={t.muted}>Looking them up in the tracker…</Body>
        ) : d.connected === false ? (
          <Body size={12.5} color={t.muted}>The tracker database isn't reachable right now.</Body>
        ) : !d.found ? (
          <>
            <SectionHead title="In the tracker" size={15} />
            <Empty icon="user" title="No tracker account">
              {email} has never signed into the app. They may have bought but not set up, or written in before ordering. Nothing to fix on the app side.
            </Empty>
          </>
        ) : (
          <>
            <SectionHead title="In the tracker" size={15} sub="Matched by email address." right={<Badge tone={STATUS_META[d.person.status]?.[1]}>{STATUS_META[d.person.status]?.[0]}</Badge>} />
            <Body size={13.5} style={{ color: t.fg }}>{d.person.summary}</Body>
            {d.person.flags.length > 0 && (
              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                {d.person.flags.map((f: any, i: number) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}><Icon name="alert" size={13} color={t.bad} /><span style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 12.5, color: t.bad }}>{f.text}</span></div>
                ))}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 12, marginTop: 14 }}>
              <Field label="Reminders">{d.person.remindersOn ? (d.person.hasPush ? "On · " + (d.person.reminderLabel ?? "") : "On, but no push") : "Off"}</Field>
              <Field label="Last opened">{d.person.lastOpen ? relDays(d.person.daysSinceOpen) : "No visits recorded"}</Field>
              <Field label="Last dose">{d.person.lastLog ? fmtDay(d.person.lastLog) : "None yet"}</Field>
              <Field label="Where they are">{whereLabel(d.person) ?? STATUS_META[d.person.status]?.[0]}</Field>
            </div>
            <Pill icon="arrowRight" style={{ marginTop: 16, width: "100%" }} onClick={() => go("#/app/customers/" + d.person.id)}>View in tracker</Pill>
          </>
        )}
      </Card>
      {d?.found && (
        <Card pad={16}>
          <SectionHead title="Their other messages" size={15} />
          {otherThreads.length ? otherThreads.slice(0, 6).map((th: any) => (
            <button key={th.account + th.threadId} onClick={() => go(`#/support/${th.account}/${th.threadId}`)} style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", borderTop: "1px solid " + t.border, padding: "10px 0", cursor: "pointer", fontFamily: FIGTREE, fontSize: 13, color: t.fg2 }}>
              {th.subject || "(no subject)"} <span style={{ color: t.muted }}>· {fmtDay(th.receivedAt)}</span>
            </button>
          )) : <Body size={12.5} color={t.muted}>{others.loading ? "Checking…" : "This is their only thread."}</Body>}
        </Card>
      )}
    </div>
  );
}

